import React, { useState } from 'react';
import {
  CheckBoxesList,
  CheckboxListItem,
} from '../CheckBoxesList/CheckBoxList';
import { ExpandableWidgetContainer } from '../ExpandableWidgetContainer';
import { SearchInput } from '../SearchInput/SearchInput';
import { TagItem } from '../TagItem/TagItem';
import './FilterableMultiSelect.scss';

export type FilterableMultiSelectItem = {
  key: string;
  name: string;
};
type Props = {
  className?: string;
  disableDeleting?: boolean;
  originalItems?: string[];
  heading: string;
  searchPlaceholder?: string;
  items: FilterableMultiSelectItem[];
  selectedKeys: string[];
  closeByChange?: boolean;
  onChange: (selectedKeys: string[]) => void;
  showBadge?: boolean;
  showSearch?: boolean;
  isOnChangeInOnClick?: boolean;
  inputClassName?: string;
};

const useFilterableMultiSelectProps = ({ selectedKeys, onChange }: Props) => {
  const [filter, setFilter] = useState('');

  const onChangeCheckboxList = (items: CheckboxListItem[]) => {
    const newSelection = new Set(selectedKeys);
    items.forEach(i => {
      if (i.isChecked) {
        newSelection.add(i.key);
      } else {
        newSelection.delete(i.key);
      }
    });

    onChange(Array.from(newSelection));
  };

  const onTagDelete = (deletedKey: string) => () => {
    onChange(selectedKeys.filter(k => k !== deletedKey));
  };

  return {
    filter,
    setFilter,
    onChangeCheckboxList,
    onTagDelete,
  };
};
const nameForKey = (
  items: FilterableMultiSelectItem[],
  key: string,
): string => {
  const matchingItem = items.find(i => i.key === key);
  if (matchingItem) {
    return matchingItem.name;
  }
  return key;
};

export const FilterableMultiSelect = (props: Props) => {
  const {
    searchPlaceholder,
    className,
    heading,
    items,
    selectedKeys,
    closeByChange,
    disableDeleting,
    originalItems,
    showBadge = true,
    showSearch = true,
    isOnChangeInOnClick,
    inputClassName,
  } = props;

  const { filter, setFilter, onTagDelete, onChangeCheckboxList } =
    useFilterableMultiSelectProps(props);

  const containerContent = (
    expanded: boolean,
    setExpanded: (expand: boolean) => void,
  ) => {
    const onChangeCheckboxListProxy = (newItems: CheckboxListItem[]) => {
      if (closeByChange) {
        setExpanded(false);
        setFilter('');
      }
      onChangeCheckboxList(newItems);
    };

    const canDeleteItem = (key: string) => {
      return !(disableDeleting && originalItems && originalItems.includes(key));
    };

    return (
      <>
        <div
          className="filterable-multi-select__heading"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="filterable-multi-select__heading-text">{heading}</div>
          {showBadge && selectedKeys.length ? (
            <div className="filterable-multi-select__heading-badge">
              {selectedKeys.length}
            </div>
          ) : null}
          <div className="filterable-multi-select__toggle">
            <i
              className={`fas fa-chevron-down ${
                expanded ? 'fa-rotate-180' : ''
              }`}
            />
          </div>
        </div>
        {expanded && (
          <div className="filterable-multi-select__expanded-area">
            {showBadge && selectedKeys.length > 0 && (
              <div className="filterable-multi-select__tags">
                {selectedKeys.map(k => (
                  <TagItem
                    text={nameForKey(items, k)}
                    preventDelete={!canDeleteItem(k)}
                    key={k}
                    onDelete={onTagDelete(k)}
                  />
                ))}
              </div>
            )}
            <div className="filterable-multi-select__checkboxes">
              {showSearch && (
                <SearchInput
                  placeholder={searchPlaceholder}
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  inputClassName={inputClassName}
                />
              )}
              <CheckBoxesList
                onChangeCheckbox={onChangeCheckboxListProxy}
                disabled={false}
                noSelectAll
                items={items
                  .filter(
                    i =>
                      i.name
                        .toLocaleLowerCase()
                        .indexOf(filter.toLocaleLowerCase()) >= 0,
                  )
                  .map(i => ({
                    key: i.key,
                    label: i.name,
                    disabled: !canDeleteItem(i.key),
                    isChecked: selectedKeys.some(k => k === i.key),
                  }))}
                isOnChangeInOnClick={isOnChangeInOnClick}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <ExpandableWidgetContainer
      className={`filterable-multi-select ${className || ''}`}
    >
      {containerContent}
    </ExpandableWidgetContainer>
  );
};
